import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { authenticationService } from "services";
import { AuthenticationContext } from "contexts";

export function AuthenticationProvider({ children }) {
 const [user, setUser] = useState(undefined); // undefined = loading, null = not authenticated
 const [userProfile, setUserProfile] = useState(null);
 const [isButtonLoading, setIsButtonLoading] = useState(false);

 // Refs
 const mountedRef = useRef(true);
 const authUnsubscribeRef = useRef(null);
 const hasCheckedInitialAuth = useRef(false);

 useEffect(() => {
  mountedRef.current = true;
  return () => {
   mountedRef.current = false;
   if (typeof authUnsubscribeRef.current === "function") {
    authUnsubscribeRef.current();
   }
  };
 }, []);

 const fetchUserProfile = useCallback(async (uid) => {
  try {
   const profileData = await authenticationService.getUserProfile(uid);
   setUserProfile(profileData);
   return profileData;
  } catch (error) {
   console.error("Failed to fetch user profile:", error);
   setUserProfile(null);
   return null;
  }
 }, []);

 // Seed userProfile from a server session response — avoids a separate
 // Firestore read when /auth/status already returns all profile fields.
 const seedProfileFromSession = useCallback((session) => {
  if (!session?.authenticated) return;
  setUserProfile({
   firstName: session.firstName || null,
   lastName: session.lastName || null,
   birthday: session.birthday || null,
   gender: session.gender || null,
   photoUrl: session.photoUrl || null,
   settings: session.settings || { theme: "light", notifications: { email: true } },
   createdAt: session.createdAt || null,
   updatedAt: session.updatedAt || null,
  });
 }, [])

 useEffect(() => {
  const initializeAuth = async () => {
   try {
    await authenticationService.initialize();

    if (!mountedRef.current) return;

    // Immediately check current user status
    const currentUser = await authenticationService.getCurrentUser();

    if (currentUser) {
     setUser(currentUser);
     hasCheckedInitialAuth.current = true;

     // Check server session first — if valid, seed profile from the response
     // to avoid a redundant Firestore read (PERF-02/03).
     // /auth/status already returns firstName, lastName, birthday, gender, settings.
     const serverSession = await authenticationService.checkServerSession();
     if (serverSession.authenticated && serverSession.uid === currentUser.uid) {
      seedProfileFromSession(serverSession);
     } else {
      // Session not valid — create one and fetch profile the old way
      authenticationService
       .syncServerSession(currentUser)
       .catch((error) => console.error("Background session sync error:", error));
      await fetchUserProfile(currentUser.uid);
     }
    } else {
     setUser(null);
     setUserProfile(null);
     hasCheckedInitialAuth.current = true;
    }

    // Set up auth state listener for future changes
    const unsubscribe = authenticationService.onAuthStateChanged(
     async (firebaseUser) => {
      if (!mountedRef.current) return;

      // Skip the initial auth state change if we've already handled it
      if (!hasCheckedInitialAuth.current && firebaseUser) {
       setUser(firebaseUser);
       await fetchUserProfile(firebaseUser.uid);
       hasCheckedInitialAuth.current = true;

       // Background session sync
       authenticationService
        .syncServerSession(firebaseUser)
        .catch(console.error);
       return;
      }

      // Handle subsequent auth state changes (sign in, sign out)
      if (firebaseUser) {
       // If signInWithEmail is actively in progress, it owns the session
       // creation and redirect. Skip everything here — fetching the profile
       // and calling syncServerSession would trigger re-renders and concurrent
       // fetches that Safari aborts when navigation fires immediately after.
       if (authenticationService._signingIn) return;

       setUser(firebaseUser);
       await fetchUserProfile(firebaseUser.uid);

       // For new sign-ins, we do want to ensure server session is created
       try {
        const synced = await authenticationService.syncServerSession(
         firebaseUser,
        );
        if (!synced) {
         console.warn("Session sync failed for new sign-in");
        }
       } catch (error) {
        console.error("Session sync error:", error);
       }
      } else {
       setUser(null);
       setUserProfile(null);
      }
     },
    );

    authUnsubscribeRef.current = unsubscribe;
   } catch (error) {
    console.error("Auth initialization error:", error);
    if (mountedRef.current) {
     setUser(null);
     setUserProfile(null);
     hasCheckedInitialAuth.current = true;
    }
   }
  };

  initializeAuth();
 }, [fetchUserProfile, seedProfileFromSession]);

 const signIn = useCallback(async (email, password) => {
  setIsButtonLoading(true);
  try {
   const user = await authenticationService.signInWithEmail(email, password);

   // Check if there's a returnUrl and redirect
   const urlParams = new URLSearchParams(window.location.search);
   const returnUrl = urlParams.get("returnUrl");

   if (returnUrl) {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isMobileOrSafari =
     /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      navigator.userAgent.toLowerCase(),
     ) || isSafari;
    const useOpaqueCode = process.env.REACT_APP_OPAQUE_CODE_AUTH === "true";

    if (isMobileOrSafari) {
     // Ensure returnUrl is absolute — new URL() throws on relative paths
     const absoluteReturnUrl = returnUrl.startsWith('http')
      ? returnUrl
      : `${window.location.origin}${returnUrl.startsWith('/') ? '' : '/'}${returnUrl}`;
     console.log('[auth] returnUrl:', returnUrl, '→ absolute:', absoluteReturnUrl);
     const url = new URL(absoluteReturnUrl);

     if (useOpaqueCode) {
      // SEC-01 fix: use the short-lived opaque code stored in memory by
      // createServerSession(). The destination app calls /auth/exchange-code
      // to swap it for a real session cookie — no raw token in the URL.
      const code = authenticationService._pendingCode;
      console.log('[auth] pendingCode at redirect:', code ? `set (${code.slice(0,8)}...)` : 'NULL');
      if (code) {
       url.searchParams.set("code", code);
       authenticationService._pendingCode = null; // consume immediately
       console.log('[auth] redirecting to:', url.toString());
      } else {
       console.warn("[auth] No pending code available for redirect");
      }
     } else {
      // Legacy path: poll localStorage for the raw session token
      let token = null;
      let attempts = 0;
      while (!token && attempts < 50) {
       token = localStorage.getItem("mobile_session_token");
       if (!token) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
       }
      }
      if (token) url.searchParams.set("session_token", token);
     }

     window.location.href = url.toString();
     return;
    }

    // Chrome desktop: cookie lands correctly, just redirect
    await new Promise((resolve) => setTimeout(resolve, 500));
    window.location.href = returnUrl;
    return;
   }

   // No returnUrl — staying on auth domain. Set user state explicitly
   // since onAuthStateChanged skips it while _signingIn is true.
   if (mountedRef.current) {
    setUser(user);
    fetchUserProfile(user.uid).catch(console.error);
   }
   return user;
  } catch (error) {
   throw error;
  } finally {
   if (mountedRef.current) {
    setIsButtonLoading(false);
   }
  }
 }, []);

 const signOut = useCallback(async () => {
  setIsButtonLoading(true);
  try {
   await authenticationService.signOut();
   setUser(null);
   setUserProfile(null);
  } catch (error) {
   console.error("Sign out error:", error);
   setUser(null);
   setUserProfile(null);
  } finally {
   if (mountedRef.current) {
    setIsButtonLoading(false);
   }
  }
 }, []);

 const signUp = useCallback(async (email, password, profileData = null) => {
  setIsButtonLoading(true);
  try {
   const user = await authenticationService.signUp(email, password, profileData);
   return user;
  } catch (error) {
   throw error;
  } finally {
   if (mountedRef.current) {
    setIsButtonLoading(false);
   }
  }
 }, []);

 const passwordReset = useCallback(async (email) => {
  setIsButtonLoading(true);
  try {
   await authenticationService.passwordReset(email);
  } finally {
   setIsButtonLoading(false);
  }
 }, []);

 const verifyResetCode = useCallback(async (oobCode) => {
  return await authenticationService.verifyResetCode(oobCode);
 }, []);

 const resetPassword = useCallback(async (oobCode, newPassword) => {
  setIsButtonLoading(true);
  try {
   await authenticationService.confirmPasswordReset(oobCode, newPassword);
  } finally {
   setIsButtonLoading(false);
  }
 }, []);

 const verifyEmailWithCode = useCallback(async (oobCode) => {
  return await authenticationService.verifyEmailWithCode(oobCode);
 }, []);

 // Phone verification methods
 const sendPhoneVerification = useCallback(async (phoneNumber, containerId) => {
  setIsButtonLoading(true);
  try {
   const result = await authenticationService.sendPhoneVerificationToUser(
    phoneNumber,
    containerId,
   );
   return result;
  } catch (error) {
   throw error;
  } finally {
   if (mountedRef.current) {
    setIsButtonLoading(false);
   }
  }
 }, []);

 const verifyPhoneNumber = useCallback(async (verificationId, code) => {
  setIsButtonLoading(true);
  try {
   const result = await authenticationService.verifyPhoneForUser(
    verificationId,
    code,
   );
   return result;
  } catch (error) {
   throw error;
  } finally {
   if (mountedRef.current) {
    setIsButtonLoading(false);
   }
  }
 }, []);

 const validatePhone = useCallback((phoneNumber) => {
  return authenticationService.validatePhoneNumber(phoneNumber);
 }, []);

 const normalizePhone = useCallback((phoneNumber) => {
  return authenticationService.normalizePhoneNumber(phoneNumber);
 }, []);

 // Additional auth utilities
 const changePassword = useCallback(async (currentPassword, newPassword) => {
  setIsButtonLoading(true);
  try {
   await authenticationService.changePassword(currentPassword, newPassword);
  } finally {
   setIsButtonLoading(false);
  }
 }, []);

 const sendEmailVerification = useCallback(async (continueUrl) => {
  setIsButtonLoading(true);
  try {
   await authenticationService.sendEmailVerificationLink(continueUrl);
  } finally {
   setIsButtonLoading(false);
  }
 }, []);

 const isEmailVerified = useCallback(async () => {
  return await authenticationService.isEmailVerified();
 }, []);

 const disableAccount = useCallback(async () => {
  setIsButtonLoading(true);
  try {
   await authenticationService.disableAccount();
  } finally {
   setIsButtonLoading(false);
  }
 }, []);

 const deleteAccount = useCallback(async () => {
  setIsButtonLoading(true);
  try {
   await authenticationService.deleteAccount();
  } finally {
   setIsButtonLoading(false);
  }
 }, []);

 // Add computed user properties
 const computedValues = useMemo(() => {
  return {
   userEmail: user?.email || null,
   userFirstName: userProfile?.firstName || null,
   userLastName: userProfile?.lastName || null,
   userFullName:
    userProfile?.firstName && userProfile?.lastName
     ? `${userProfile.firstName} ${userProfile.lastName}`
     : userProfile?.firstName || null,
   userBirthday: userProfile?.birthday || null,
   userGender: userProfile?.gender || null,
   userSettings: userProfile?.settings || {
    theme: "light",
    notifications: { email: true },
   },
   userTheme: userProfile?.settings?.theme || "light",
   userPhotoUrl: userProfile?.photoUrl || null,
  };
 }, [user, userProfile]);

 const value = useMemo(
  () => ({
   // User state
   user,
   userProfile,
   loading: user === undefined,
   isButtonLoading,
   isAuthenticated: !!user,

   // Computed user properties
   ...computedValues,

   // Core auth methods
   signIn,
   signUp,
   signOut,

   // Password methods
   passwordReset,
   verifyResetCode,
   resetPassword,
   changePassword,

   // Email verification
   verifyEmailWithCode,
   sendEmailVerification,
   isEmailVerified,

   // Phone verification methods
   sendPhoneVerification,
   verifyPhoneNumber,
   validatePhone,
   normalizePhone,

   // Account management
   disableAccount,
   deleteAccount,
  }),
  [
   user,
   userProfile,
   computedValues,
   isButtonLoading,
   signIn,
   signUp,
   signOut,
   passwordReset,
   verifyResetCode,
   resetPassword,
   changePassword,
   verifyEmailWithCode,
   sendEmailVerification,
   isEmailVerified,
   sendPhoneVerification,
   verifyPhoneNumber,
   validatePhone,
   normalizePhone,
   disableAccount,
   deleteAccount,
  ],
 );

 return (
  <AuthenticationContext.Provider value={value}>
   {children}
  </AuthenticationContext.Provider>
 );
}
